import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Copy, Trash2, Upload } from "lucide-react";
import { useConfirm } from "@/components/admin/ConfirmDialog";

export const Route = createFileRoute("/_authenticated/admin/galeria")({
  component: AdminMedia,
});

type Row = {
  id: string;
  public_url: string;
  storage_path: string;
  file_name: string;
  alt_text: string | null;
  title: string | null;
  caption: string | null;
  description: string | null;
};

function sanitize(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .toLowerCase();
}

function AdminMedia() {
  const [rows, setRows] = useState<Row[]>([]);
  const { confirm, confirmDialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState<Row | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase.from("media").select("*").order("created_at", { ascending: false });
    setRows((data ?? []) as unknown as Row[]);
  }

  useEffect(() => {
    void load();
  }, []);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const path = `uploads/${Date.now()}-${sanitize(file.name)}`;
      const { error } = await supabase.storage.from("blog").upload(path, file, { upsert: true, contentType: file.type });
      if (error) {
        toast.error(error.message);
        continue;
      }
      const { data: pub } = supabase.storage.from("blog").getPublicUrl(path);
      await supabase.from("media").insert({
        storage_path: path,
        public_url: pub.publicUrl,
        file_name: file.name,
        mime_type: file.type,
        size_bytes: file.size,
      });
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    toast.success("Upload concluído.");
    void load();
  }

  async function remove(row: Row) {
    const ok = await confirm({
      title: "Excluir imagem da galeria?",
      description: `"${row.file_name}" será removida definitivamente e sumirá dos artigos que a utilizam.`,
      confirmLabel: "Excluir imagem",
    });
    if (!ok) return;
    await supabase.storage.from("blog").remove([row.storage_path]);
    await supabase.from("media").delete().eq("id", row.id);
    setActive(null);
    toast.success("Imagem excluída.");
    void load();
  }

  async function saveMeta(row: Row) {
    const { error } = await supabase
      .from("media")
      .update({
        alt_text: row.alt_text || null,
        title: row.title || null,
        caption: row.caption || null,
        description: row.description || null,
      })
      .eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Detalhes salvos.");
    setActive(null);
    void load();
  }

  const filtered = rows.filter((r) => r.file_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="kk-cms__page">
      <div className="kk-cms__head">
        <div>
          <h1>Galeria</h1>
          <p>{rows.length} imagens</p>
        </div>
        <label className="kk-cms__btn">
          <Upload size={15} /> {busy ? "Enviando…" : "Enviar imagens"}
          <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} />
        </label>
      </div>

      <div className="kk-cms__filters">
        <input type="text" placeholder="Buscar imagem…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="kk-picker__grid">
        {filtered.map((r) => (
          <button key={r.id} type="button" className="kk-picker__item" onClick={() => setActive(r)}>
            <img
              src={r.public_url}
              alt={r.alt_text ?? ""}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = "none";
                img.insertAdjacentHTML(
                  "afterend",
                  '<span class="kk-picker__broken">Imagem indisponível</span>',
                );
              }}
            />
            <span>{r.file_name}</span>
          </button>
        ))}
      </div>

      {active ? (
        <div className="kk-modal" onClick={() => setActive(null)}>
          <div className="kk-modal__box kk-modal__box--sm" onClick={(e) => e.stopPropagation()}>
            <div className="kk-modal__head">
              <h2>Detalhes da imagem</h2>
            </div>
            <img src={active.public_url} alt="" style={{ borderRadius: 8, marginBottom: 14 }} />
            <span className="kk-cms__label">Texto alternativo</span>
            <input type="text" value={active.alt_text ?? ""} onChange={(e) => setActive({ ...active, alt_text: e.target.value })} />
            <span className="kk-cms__label">Título</span>
            <input type="text" value={active.title ?? ""} onChange={(e) => setActive({ ...active, title: e.target.value })} />
            <span className="kk-cms__label">Legenda</span>
            <input type="text" value={active.caption ?? ""} onChange={(e) => setActive({ ...active, caption: e.target.value })} />
            <span className="kk-cms__label">Descrição</span>
            <textarea rows={3} value={active.description ?? ""} onChange={(e) => setActive({ ...active, description: e.target.value })} />
            <div className="kk-modal__foot">
              <button
                type="button"
                className="kk-cms__btn kk-cms__btn--ghost"
                onClick={() => {
                  void navigator.clipboard.writeText(active.public_url);
                  toast.success("URL copiada.");
                }}
              >
                <Copy size={15} /> Copiar URL
              </button>
              <button type="button" className="kk-cms__btn kk-cms__btn--danger" onClick={() => remove(active)}>
                <Trash2 size={15} /> Excluir
              </button>
              <button type="button" className="kk-cms__btn" onClick={() => saveMeta(active)}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {confirmDialog}
    </div>
  );
}
